import { doAction } from '../middleware/fetchMiddleware';
import { actions as merchantActions } from '../merchant/merchantActions';
import { actions as terminalActions } from '../terminal/terminalActions';
import { actions as selfActions } from '../self/selfActions';
import authClient from '../utils/authClient';

export const storageKeys = {
    username: 'username',
    refreshToken: 'refreshToken',
    accessToken: 'accessToken',
    tokenExpiration: 'tokenExpiration'
};

const clearClientSideState = () => {
    // Be defensive: these APIs might not exist in some test/runtime environments.
    try {
        if (typeof localStorage !== 'undefined') {
            // Clear any cached per-merchant terminal selection (user-ish state).
            Object.keys(localStorage).forEach((key) => {
                if (key && key.startsWith('default-terminal-')) {
                    localStorage.removeItem(key);
                }
            });
        }
    } catch {
        // ignore
    }

    try {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.clear();
        }
    } catch {
        // ignore
    }

    // If a simple-login key is present in the URL, remove it so logout "sticks" on refresh.
    try {
        if (typeof window !== 'undefined' && window.history?.replaceState) {
            const url = new URL(window.location.href);
            if (url.searchParams.has('simpleLogin')) {
                url.searchParams.delete('simpleLogin');
                window.history.replaceState({}, document.title, url.toString());
            }
        }
    } catch {
        // ignore
    }
};

const initialState = {
    isCheckingLogin: true,
    isLoggedIn: false
};

export const actionTypes = {
    LOGIN: 'login_login',
    LOGOUT: 'login_logout',
    SIMPLE_LOGIN: 'simple_login'
};

const loginError = (error, dispatch) => {
    dispatch({
        type: doAction.error(actionTypes.LOGIN),
        payload: error
    });
};

const login = (username, password) => (dispatch) => {
    authClient.loginWithCredentials(username, password)
        .then(({ username: name, access }) => {
            dispatch({
                type: actionTypes.LOGIN,
                payload: { username: name, access }
            });
            dispatch(merchantActions.getMerchants());
            dispatch(merchantActions.getReportingMerchants());
            dispatch(terminalActions.getTerminals());
            dispatch(selfActions.getSelf());
        })
        .catch((err) => {
            loginError(err.message || 'Login failed', dispatch);
        });
};

const logout = () => async (dispatch) => {
    authClient.clear();
    clearClientSideState();
    dispatch({
        type: actionTypes.LOGOUT
    });
};

const refresh = () => async (dispatch) => {
    authClient.refreshSession()
        .then(({ username, access }) => {
            dispatch({
                type: actionTypes.LOGIN,
                payload: { username, access }
            });
            dispatch(merchantActions.getMerchants());
            dispatch(merchantActions.getReportingMerchants());
            dispatch(terminalActions.getTerminals());
            dispatch(selfActions.getSelf());
        })
        .catch(() => {
            dispatch(logout());
        });
};

const simpleLogin = (key) => (dispatch) => {
    dispatch({ type: doAction.fetch(actionTypes.SIMPLE_LOGIN) });
    authClient.simpleLogin(key)
        .then(({ username, access }) => {
            dispatch({ type: doAction.success(actionTypes.SIMPLE_LOGIN), payload: { username, access } });
            dispatch({
                type: actionTypes.LOGIN,
                payload: { username, access }
            });
            dispatch(merchantActions.getMerchants());
            dispatch(merchantActions.getReportingMerchants());
            dispatch(terminalActions.getTerminals());
            dispatch(selfActions.getSelf());
        })
        .catch(() => {
            dispatch({ type: doAction.error(actionTypes.SIMPLE_LOGIN) });
            dispatch(logout());
        });
};

export const actions = {
    login,
    logout,
    refresh,
    simpleLogin
};

export function reducers(state = initialState, action) {
    switch (action.type) {
        case actionTypes.LOGIN:
            return {
                ...state,
                ...action.payload,
                isCheckingLogin: false,
                isLoggedIn: true
            };
        case actionTypes.LOGOUT:
            return {
                ...initialState,
                isCheckingLogin: false
            };
        case doAction.success(actionTypes.SIMPLE_LOGIN):
            // Session is saved in simpleLoginSuccess (onSuccess) and then refreshed.
            return {
                ...state,
                ...action.payload
            };
        case doAction.error(actionTypes.LOGIN):
            return {
                ...initialState,
                isCheckingLogin: false,
                loginError: action.payload
            };

        default:
            return state;
    }
}
